import { useGlobalStore } from '@/stores/global';

const MAX_RETRIES = 5;
const RETRY_DELAY_MS = 1000;
const RETRY_DELAY_MAX_MS = 10000;

const getRetryDelay = (e, attempt) => {
  const retryAfter = Number(e?.response?.headers?.get('Retry-After'));
  if (Number.isFinite(retryAfter)) return retryAfter * 1000;
  return Math.min(RETRY_DELAY_MS * 2 ** attempt, RETRY_DELAY_MAX_MS);
};

// Shared by layouts/default.vue and server/plugins/prefetchCriticalData so
// both call the exact same function — the plugin prefetches it under the
// same useAsyncData key ('globalSettings'), and since Nuxt dedupes by key,
// the layout's own call just reuses that cached result without re-invoking
// this (and re-running its setApplicationData/setLinks/setApiKeys side
// effects) a second time.
export async function fetchSettingsWithRetry(retries = 0) {
  const globalStore = useGlobalStore();
  try {
    const res = await globalStore.fetchData('settings', { retry: 0 });
    if (!res) throw new Error('Empty response from settings API');

    if (res.redirect?.redirect_url) {
      return res;
    }

    globalStore.setApplicationData(res.application);
    globalStore.setLinks(res.terms_of_use_link, res.privacy_policy_link);
    globalStore.setApiKeys({
      googleMapsApiKey: res.google_maps_api_key,
      captchaSiteKey: res?.google_captcha_site_key ?? '',
    });

    return res;
  } catch (e) {
    if (retries < MAX_RETRIES) {
      const delay = getRetryDelay(e, retries);
      console.warn(
        `[settings] Attempt ${retries + 1} failed, retrying in ${delay}ms...`,
        e,
      );
      await new Promise((resolve) => setTimeout(resolve, delay));
      return fetchSettingsWithRetry(retries + 1);
    }

    console.error(`[settings] All ${MAX_RETRIES} retries failed.`, e);
    return null;
  }
}
